import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, Chip, FormControl, InputLabel, MenuItem, OutlinedInput,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, useTheme, FormControlLabel, Checkbox, Tabs, Tab,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { reportsApi } from "../shared/api";

type OptionItem = { id?: number; value?: string; label: string };

type StudentRow = {
  student_id: number;
  student_name: string;
  reference_id: string | null;
  grade: string | null;
  subject: string | null;
};

type ClassRow = {
  occurrence_id: number;
  class_id: number;
  start_time: string | null;
  end_time: string | null;
  mode: string | null;
  location: string | null;
  capacity: number | null;
  enrolled_count: number;
  students: StudentRow[];
};

type DayGroup = {
  date: string;
  weekday: string;
  staff: Array<{ staff_id: number | null; staff_name: string; classes: ClassRow[] }>;
};

type Filters = {
  from_date: string;
  to_date: string;
  location_id: number[];
  staff_id: number[];
  study_mode: string;
  program_type: string;
  class_type: string;
  include_zero_student: boolean;
  include_summary: boolean;
};

function startOfWeekMonday(d: Date): string {
  const dt = new Date(d);
  const dow = (dt.getDay() + 6) % 7; // Monday = 0
  dt.setDate(dt.getDate() - dow);
  return dt.toISOString().slice(0, 10);
}
function endOfWeekSunday(d: Date): string {
  const dt = new Date(d);
  const dow = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() + (6 - dow));
  return dt.toISOString().slice(0, 10);
}
function addDays(iso: string, n: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtTime(t: string | null | undefined): string {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  const mer = hh >= 12 ? "PM" : "AM";
  return `${hh % 12 || 12}:${m} ${mer}`;
}
function fmtDayHeading(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

const defaultFilters = (): Filters => {
  const today = new Date();
  return {
    from_date: startOfWeekMonday(today),
    to_date:   endOfWeekSunday(today),
    location_id: [],
    staff_id: [],
    study_mode: "",
    program_type: "",
    class_type: "",
    include_zero_student: false,
    include_summary: true,
  };
};

function toParams(f: Filters) {
  return {
    from_date: f.from_date,
    to_date:   f.to_date,
    location_id: f.location_id.length ? f.location_id : undefined,
    staff_id:    f.staff_id.length ? f.staff_id : undefined,
    study_mode:  f.study_mode || undefined,
    program_type: f.program_type || undefined,
    class_type:   f.class_type || undefined,
    include_zero_student: f.include_zero_student ? 1 : undefined,
    include_summary: f.include_summary ? 1 : 0,
  };
}

export default function RegularClassScheduleReportPage() {
  const theme = useTheme();
  const [filters, setFilters] = useState<Filters>(defaultFilters());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<null | "pdf" | "csv">(null);

  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [summary, setSummary] = useState<Record<string, any>>({});
  const [days, setDays] = useState<DayGroup[]>([]);
  const [activeDay, setActiveDay] = useState<string>("");

  const [options, setOptions] = useState<{
    staff: OptionItem[];
    locations: OptionItem[];
    study_modes: OptionItem[];
    program_types: string[];
    class_types: string[];
  }>({ staff: [], locations: [], study_modes: [], program_types: [], class_types: [] });

  // Load filter options once.
  useEffect(() => {
    let alive = true;
    reportsApi.regularClassScheduleOptions()
      .then((res) => { if (alive) setOptions(res); })
      .catch((e) => { if (alive) setError(e.message || String(e)); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await reportsApi.regularClassSchedulePreview(toParams(filters));
      setRange(res.range);
      setSummary(res.summary || {});
      setDays(res.days || []);
      // Keep the active day tab if it's still in the range; otherwise pick
      // the first day that has classes, else the first day of the range.
      const stillThere = res.days.find((d) => d.date === activeDay);
      if (!stillThere) {
        const firstPop = res.days.find((d) => d.staff.length > 0);
        setActiveDay((firstPop ?? res.days[0])?.date ?? "");
      }
    } catch (e: any) {
      setError(e.message || String(e));
      setDays([]); setSummary({});
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const quickRange = (kind: "this_week" | "next_week" | "this_month") => {
    const today = new Date();
    if (kind === "this_week") {
      setFilters((f) => ({ ...f, from_date: startOfWeekMonday(today), to_date: endOfWeekSunday(today) }));
    } else if (kind === "next_week") {
      const nw = new Date(today); nw.setDate(nw.getDate() + 7);
      setFilters((f) => ({ ...f, from_date: startOfWeekMonday(nw), to_date: endOfWeekSunday(nw) }));
    } else if (kind === "this_month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end   = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setFilters((f) => ({ ...f, from_date: start.toISOString().slice(0, 10), to_date: end.toISOString().slice(0, 10) }));
    }
  };

  const clearFilters = () => setFilters(defaultFilters());

  const runExport = async (kind: "pdf" | "csv") => {
    setExporting(kind);
    try {
      const params = toParams(filters);
      if (kind === "pdf") await reportsApi.regularClassSchedulePdf(params);
      else                await reportsApi.regularClassScheduleCsv(params);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setExporting(null);
    }
  };

  const dayView = useMemo(
    () => days.find((d) => d.date === activeDay) ?? null,
    [days, activeDay],
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1600, mx: "auto" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1b3a6b" }}>
            Regular Class Schedule Report
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Weekday-grouped verification list for regular scheduled classes. Makeup, cancelled, released
            and rescheduled sessions are excluded.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={load}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => window.print()}
            sx={{ textTransform: "none" }}
          >
            Print
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => runExport("csv")}
            disabled={!!exporting}
            sx={{ textTransform: "none" }}
          >
            {exporting === "csv" ? "…" : "CSV"}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={() => runExport("pdf")}
            disabled={!!exporting}
            sx={{ textTransform: "none", bgcolor: "#1b3a6b", "&:hover": { bgcolor: "#152e57" } }}
          >
            {exporting === "pdf" ? "Generating…" : "PDF"}
          </Button>
        </Stack>
      </Stack>

      {/* ── Filters ────────────────────────────────────────────────── */}
      <Card sx={{ p: 2, mb: 2, border: "1px solid #e2e8f0" }} elevation={0}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} flexWrap="wrap" useFlexGap>
          <TextField
            size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.from_date}
            onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
            sx={{ minWidth: 150 }}
          />
          <TextField
            size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.to_date}
            onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
            sx={{ minWidth: 150 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Location</InputLabel>
            <Select
              multiple label="Location"
              value={filters.location_id}
              onChange={(e) => setFilters((f) => ({ ...f, location_id: (e.target.value as number[]) }))}
              input={<OutlinedInput label="Location" />}
              renderValue={(sel) => (sel as number[]).map((id) => options.locations.find((l) => l.id === id)?.label ?? id).join(", ")}
            >
              {options.locations.map((l) => (
                <MenuItem key={l.id} value={l.id!}>{l.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Staff</InputLabel>
            <Select
              multiple label="Staff"
              value={filters.staff_id}
              onChange={(e) => setFilters((f) => ({ ...f, staff_id: (e.target.value as number[]) }))}
              input={<OutlinedInput label="Staff" />}
              renderValue={(sel) => (sel as number[]).map((id) => options.staff.find((l) => l.id === id)?.label ?? id).join(", ")}
            >
              {options.staff.map((s) => (
                <MenuItem key={s.id} value={s.id!}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Mode</InputLabel>
            <Select
              label="Mode"
              value={filters.study_mode}
              onChange={(e) => setFilters((f) => ({ ...f, study_mode: e.target.value as string }))}
            >
              <MenuItem value="">All</MenuItem>
              {options.study_modes.map((m) => (
                <MenuItem key={m.value} value={m.value!}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Enrollment Type</InputLabel>
            <Select
              label="Enrollment Type"
              value={filters.program_type}
              onChange={(e) => setFilters((f) => ({ ...f, program_type: e.target.value as string }))}
            >
              <MenuItem value="">All</MenuItem>
              {options.program_types.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Class Type</InputLabel>
            <Select
              label="Class Type"
              value={filters.class_type}
              onChange={(e) => setFilters((f) => ({ ...f, class_type: e.target.value as string }))}
            >
              <MenuItem value="">All</MenuItem>
              {options.class_types.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={<Checkbox checked={filters.include_zero_student} onChange={(e) => setFilters((f) => ({ ...f, include_zero_student: e.target.checked }))} />}
            label="Include zero-student classes"
          />
          <FormControlLabel
            control={<Checkbox checked={filters.include_summary} onChange={(e) => setFilters((f) => ({ ...f, include_summary: e.target.checked }))} />}
            label="Include summary in PDF"
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
          <Button size="small" variant="text" onClick={() => quickRange("this_week")}>This week</Button>
          <Button size="small" variant="text" onClick={() => quickRange("next_week")}>Next week</Button>
          <Button size="small" variant="text" onClick={() => quickRange("this_month")}>This month</Button>
          <Button size="small" variant="text" color="inherit" onClick={clearFilters}>Clear filters</Button>
          {range && (
            <Chip size="small" variant="outlined" label={`Range: ${range.from} → ${range.to}`} sx={{ ml: "auto" }} />
          )}
        </Stack>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Summary cards ─────────────────────────────────────────── */}
      <Stack direction="row" spacing={1.25} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1.25 }}>
        {[
          ["Classes",           summary.total_classes ?? 0],
          ["Distinct Students", summary.total_distinct_students ?? 0],
          ["Staff",             summary.total_staff ?? 0],
          ["Online",            summary.online_classes ?? 0],
          ["In-Person",         summary.in_person_classes ?? 0],
          ["Hybrid",            summary.hybrid_classes ?? 0],
          ["Zero-student",      summary.zero_student_classes ?? 0],
        ].map(([label, value]) => (
          <Card key={String(label)} elevation={0} sx={{ p: 1.25, minWidth: 130, border: "1px solid #e2e8f0", textAlign: "center" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, color: "text.secondary", textTransform: "uppercase" }}>
              {label as string}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1b3a6b", mt: 0.25 }}>
              {value as number}
            </Typography>
          </Card>
        ))}
      </Stack>

      {/* ── Day tabs ──────────────────────────────────────────────── */}
      <Tabs
        value={activeDay || false}
        onChange={(_, v) => setActiveDay(v as string)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: "1px solid #e2e8f0", mb: 1.5, minHeight: 40 }}
      >
        {days.map((d) => (
          <Tab
            key={d.date}
            value={d.date}
            sx={{ textTransform: "none", minHeight: 40, fontSize: 12.5 }}
            label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>{d.weekday.slice(0, 3)}</span>
                <span style={{ color: "#64748b" }}>{d.date.slice(5)}</span>
                <Chip
                  size="small"
                  label={d.staff.reduce((n, s) => n + s.classes.length, 0)}
                  sx={{ height: 18, fontSize: 10, bgcolor: "#eff6ff", color: "#1b3a6b" }}
                />
              </Stack>
            }
          />
        ))}
      </Tabs>

      {/* ── Day content: staff-grouped classes ────────────────────── */}
      {!dayView && !loading && (
        <Typography sx={{ color: "text.secondary", p: 3, textAlign: "center" }}>
          Pick a day to see its regular class schedule.
        </Typography>
      )}
      {dayView && (
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#1b3a6b", mb: 1 }}>
            {fmtDayHeading(dayView.date)}
          </Typography>
          {dayView.staff.length === 0 ? (
            <Typography sx={{ color: "text.secondary", p: 3, textAlign: "center" }}>
              No regular classes on this day for the selected filters.
            </Typography>
          ) : (
            dayView.staff.map((sg) => (
              <Box key={String(sg.staff_id)} sx={{ mb: 2 }}>
                <Box sx={{ px: 1.25, py: 0.75, bgcolor: "#eff6ff", borderLeft: "4px solid #1b3a6b", fontWeight: 700, color: "#1b3a6b" }}>
                  {sg.staff_name}
                </Box>
                <TableContainer component={Card} elevation={0} sx={{ border: "1px solid #e2e8f0", borderTop: "none" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell sx={{ width: 130, fontWeight: 700 }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Students · Grade · Subject</TableCell>
                        <TableCell sx={{ width: 100, fontWeight: 700 }}>Mode</TableCell>
                        <TableCell sx={{ width: 120, fontWeight: 700 }}>Location</TableCell>
                        <TableCell sx={{ width: 80, fontWeight: 700, textAlign: "center" }}>Capacity</TableCell>
                        <TableCell sx={{ width: 120, fontWeight: 700, fontSize: 11 }}>Class / Occ ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sg.classes.map((c) => (
                        <TableRow key={c.occurrence_id} hover>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            <Box sx={{ fontWeight: 700 }}>{fmtTime(c.start_time)}</Box>
                            <Box sx={{ color: "text.secondary", fontSize: 12 }}>{fmtTime(c.end_time)}</Box>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>
                            {c.students.length === 0 ? (
                              <em style={{ color: "#94a3b8" }}>No students assigned</em>
                            ) : (
                              c.students.map((s, i) => (
                                <Box key={s.student_id} sx={{ fontSize: 13, lineHeight: 1.4 }}>
                                  {i + 1}. {s.student_name}
                                  {s.grade    && <> — GR {s.grade}</>}
                                  {s.subject  && <> — {s.subject}</>}
                                </Box>
                              ))
                            )}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>{c.mode ?? "—"}</TableCell>
                          <TableCell sx={{ verticalAlign: "top" }}>{c.location ?? "—"}</TableCell>
                          <TableCell sx={{ textAlign: "center", verticalAlign: "top" }}>
                            {c.enrolled_count} / {c.capacity ?? "—"}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", fontSize: 11, color: "text.secondary" }}>
                            #{c.class_id}<br />Occ #{c.occurrence_id}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          )}
        </Box>
      )}

      {loading && (
        <Typography sx={{ color: "text.secondary", p: 2, textAlign: "center" }}>Loading…</Typography>
      )}
    </Box>
  );
}
